import { Box } from "@mui/material"
import {
  FocusContext,
  getCurrentFocusKey,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import _ from "lodash"
import React, {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useState,
} from "react"
import { s } from "src/utilities/common"

import { Footer } from "../../components/common/Footer"
import { ProgressSpinner } from "../../components/ProgressSpinner"
import {
  MyStageSliders,
  useMystageScreen,
} from "../../hooks/use-mystage-screen"
import { useRootPageTracking } from "../../hooks/use-root-page-tracking"
import useSdk, { ErrorLevel, MyStagePageQuery } from "../../hooks/use-sdk"
import { appStore } from "../../store/AppStore"
import { dimensions, maxSliderSize } from "../../utilities/constants"
import { useFocusHelper } from "../../utilities/use-focus-helper"
import { useRouteMatch } from "../../utilities/use-route-match"

import { MyStageSlider } from "./components/MyStageSlider"
import { EmptyScreen } from "./EmptyScreen"

const Screen = memo(({ visible }: { visible: boolean }) => {
  const isMyLocation = useRouteMatch()
  const setSpinner = appStore((state) => state.setSpinner)
  const data = appStore((state) => state.myStage)
  useLayoutEffect(() => {
    if (data && isMyLocation && visible) {
      setSpinner("out")
    }
  }, [isMyLocation])

  const { sliders } = useMystageScreen(data)

  const isMyRoute = useRouteMatch()
  if (!(data && sliders)) return null
  return (
    <MemoizedScreen sliders={sliders} visible={visible} isMyRoute={isMyRoute} />
  )
})

interface Props {
  sliders: MyStageSliders
  visible: boolean
  isMyRoute: boolean
}

const MemoizedScreen = memo(
  ({ sliders, visible, isMyRoute }: Props) => {
    const { getFocus, addFocus } = useFocusHelper()
    useEffect(() => {
      const currentFocusKey = getCurrentFocusKey()
      if (
        isMyRoute &&
        visible &&
        currentFocusKey &&
        !currentFocusKey.startsWith("TABBAR")
      ) {
        getFocus()
      }
    }, [isMyRoute, visible])
    const { focusKey, ref } = useFocusable({
      saveLastFocusedChild: false,
      focusable: visible && isMyRoute,
    })
    return (
      <Box
        sx={{
          overflow: "visible",
          width: dimensions.width,
        }}
      >
        <FocusContext.Provider value={focusKey}>
          <Box
            ref={ref}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "min-content",
              pb: s("2px"),
              pt: s("2px"),
            }}
          >
            {sliders.map((slider, i) => (
              <MyStageSlider
                key={"MyStageSlider" + i}
                slider={slider}
                hasMargin={i < sliders.length}
                addFocus={addFocus}
                index={i}
              />
            ))}
          </Box>
        </FocusContext.Provider>
        <Footer />
      </Box>
    )
  },
  (prevProps, nextProps) => {
    // memoize on everything except navigation stuff
    return (
      _.isEqual(prevProps.sliders, nextProps.sliders) &&
      prevProps.visible === nextProps.visible &&
      prevProps.isMyRoute === nextProps.isMyRoute
    )
  },
)

export const MyStageScreen = ({ slug }: { slug: string }) => {
  const sdk = useSdk(ErrorLevel.WithPrompt)
  const setMyStage = appStore((state) => state.setMyStage)
  const myStage = appStore((state) => state.myStage)

  /**
   * add another data loading stage that only happens when the page becomes
   * visible, to avoid rendering the MyStage page during initial load. This
   * became necessary when appStore.myStage was loaded at app start to enable
   * in-player favoriting.
   */
  const [pageData, setPageData] = useState<MyStagePageQuery>()
  //

  const finalData = useDeferredValue(pageData)
  const accessToken = appStore((state) => state.accessTokenState)
  const setSpinner = appStore((state) => state.setSpinner)
  const currentUser = appStore((state) => state.currentUser)
  const storeSlug = appStore((state) => state.slug)
  const [visible, setVisible] = useState(false)

  useRootPageTracking(visible, "MyStage", "my-stage")

  useEffect(() => {
    setVisible(slug === storeSlug)
  }, [storeSlug])

  useEffect(() => {
    if (!currentUser) {
      setMyStage(undefined)
    }
  }, [currentUser])

  useEffect(() => {
    if (visible) {
      if (!finalData) load(true)
      setSpinner("out")
    }
  }, [visible])

  useEffect(() => {
    if (visible && myStage) {
      setPageData(myStage)
    }
  }, [visible, myStage])

  const load = useCallback(
    async (force = false) => {
      if (!force && myStage) return
      document.getElementById("root")!.style.transform = "translate3d(0, 0, 0)"
      if (!currentUser) return
      const result = await sdk.myStagePage({
        sliderPageSize: maxSliderSize,
        cursor: undefined,
      })
      setMyStage(result)
    },
    [currentUser],
  )

  useEffect(() => {
    if (currentUser && finalData) load(true)
  }, [currentUser, accessToken])

  if (!currentUser) return <EmptyScreen visible={visible} />
  if (!finalData) {
    return <ProgressSpinner />
  }
  return <Screen visible={visible} />
}
